import React from "react";
import { createPortal } from "react-dom";
import { Eye, EyeOff, X } from "lucide-react";

export function Button({ children, variant = "primary", className = "", ...p }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium lift disabled:opacity-50 disabled:pointer-events-none";
  const styles = {
    primary: "bg-[var(--primary)] text-white hover:bg-[var(--primary-hover)]",
    ghost: "bg-transparent border border-black/15 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/5",
    danger: "bg-[#DA1E28] text-white hover:opacity-90",
    subtle: "bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15",
  };
  return <button className={`${base} ${styles[variant]} ${className}`} {...p}>{children}</button>;
}

export function Input({ label, className = "", type = "text", ...p }) {
  const [show, setShow] = React.useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && show ? "text" : type;
  return (
    <label className="block w-full">
      {label && <span className="mb-1 block text-xs font-medium opacity-70">{label}</span>}
      <span className="relative block">
        <input
          type={effectiveType}
          className={`w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)] ${isPassword ? "pe-10" : ""} ${className}`}
          {...p}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            tabIndex={-1}
            aria-label={show ? "Hide password" : "Show password"}
            title={show ? "Hide password" : "Show password"}
            data-testid="toggle-password-visibility"
            className="absolute end-2 top-1/2 -translate-y-1/2 opacity-55 hover:opacity-100"
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </span>
    </label>
  );
}

export function Select({ label, children, className = "", ...p }) {
  return (
    <label className="block w-full">
      {label && <span className="mb-1 block text-xs font-medium opacity-70">{label}</span>}
      <select className={`w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none focus:border-[var(--primary)] ${className}`} {...p}>
        {children}
      </select>
    </label>
  );
}

export function Card({ children, className = "", ...p }) {
  return <div className={`glass rounded-xl p-6 lift ${className}`} {...p}>{children}</div>;
}

export function Modal({ open, onClose, title, children, testid, wide = false, draggable = false }) {
  const [pos, setPos] = React.useState({ x: 0, y: 0 });
  const drag = React.useRef(null);
  const panelRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    setPos({ x: 0, y: 0 });
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  React.useEffect(() => {
    if (!draggable) return;
    const clamp = (x, y) => {
      const el = panelRef.current;
      if (!el) return { x, y };
      const maxX = Math.max(0, (window.innerWidth - el.offsetWidth) / 2 - 8);
      const maxY = Math.max(0, (window.innerHeight - el.offsetHeight) / 2 - 8);
      return { x: Math.min(maxX, Math.max(-maxX, x)), y: Math.min(maxY, Math.max(-maxY, y)) };
    };
    const onMove = (e) => {
      if (!drag.current) return;
      const p = e.touches ? e.touches[0] : e;
      setPos(clamp(drag.current.ox + (p.clientX - drag.current.sx), drag.current.oy + (p.clientY - drag.current.sy)));
    };
    const onUp = () => { drag.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [draggable]);

  const startDrag = (e) => {
    if (!draggable) return;
    const p = e.touches ? e.touches[0] : e;
    drag.current = { sx: p.clientX, sy: p.clientY, ox: pos.x, oy: pos.y };
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid={`${testid}-overlay`}>
      <div
        ref={panelRef}
        className={`w-full ${wide === "xl" ? "max-w-[105rem]" : wide ? "max-w-3xl" : "max-w-lg"} rounded-xl border border-black/10 bg-white p-6 shadow-2xl fadeup max-h-[90vh] overflow-y-auto dark:border-white/10 dark:bg-neutral-900`}
        style={draggable ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : undefined}
        onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div
          className={`mb-4 flex items-start justify-between gap-3 ${draggable ? "cursor-move select-none" : ""}`}
          onMouseDown={startDrag} onTouchStart={startDrag} data-testid={`${testid}-header`}>
          <h3 className="text-xl">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" title="Close"
            data-testid={`${testid}-close`}
            className="-me-1 -mt-1 shrink-0 rounded-md p-1 opacity-60 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

export function Badge({ children, color = "neutral", ...rest }) {
  const c = {
    neutral: "bg-black/10 dark:bg-white/10",
    success: "bg-[#24A148]/15 text-[#24A148]",
    warning: "bg-[#F1C21B]/20 text-[#9e7b00] dark:text-[#F1C21B]",
    danger: "bg-[#DA1E28]/15 text-[#DA1E28]",
    info: "bg-[var(--primary)]/15 text-[var(--primary)]",
  };
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${c[color]}`} {...rest}>{children}</span>;
}
